import React from 'react';
import styled, { css } from 'styled-components';
import { rem, rgba } from 'polished';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

export const Container = styled(Grid)`
  padding: ${rem('16px')};
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
  height: calc(100vh - 105px);
  background-color: #f2f3f5;
  
  body[data-theme='dark'] & {
    background-color: transparent;
  }

  @media (max-width: 1023px) {
    /* header 50 + breadcrumbs 55 + mobile tabs 48 */
    height: calc(100vh - 153px);
    padding: ${rem('12px')} ${rem('16px')};
  }
`;

/** Franja tabs + search (SCI summary). */
export const SummaryTabsRow = styled.div`
  background-color: #f2f3f5;
  
  body[data-theme='dark'] & {
    background-color: transparent;
  }
  margin: 0 ${rem('-16px')};
  padding: 0 ${rem('16px')} 0;

  @media (max-width: 1023px) {
    margin: 0 ${rem('-16px')};
    padding: 0 ${rem('16px')} ${rem('8px')};
  }
`;

/** Tabs + search row layout; search ~400px like SCI. */
export const SummaryTabsInner = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${rem('16px')};
  width: 100%;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  
  body[data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
  @media (max-width: 1023px) {
    align-items: center;
    border-bottom: none;
    justify-content: flex-end;
  }
`;

export const SummaryTabsWrap = styled.div`
  flex: 1 1 auto;
  min-width: 0;

  @media (max-width: 1023px) {
    display: none;
  }

  .MuiTabs-root {
    min-height: auto;
    background-color: transparent;

    &::before {
      display: none;
    }
  }

  .MuiTabs-flexContainer {
    gap: 0;
    justify-content: flex-start;
  }

  .MuiTab-root {
    min-width: 0;
    min-height: auto;
    height: auto;
    margin: 0;
    margin-top: 0;
    padding: 0;
    text-transform: none;
    font-size: ${rem('14px')};
    font-weight: 400;
    border-radius: 0;
    background: transparent;
    color: ${props => props.theme.colors.S['800']};
    
    body[data-theme='dark'] & {
      color: rgba(255, 255, 255, 0.78);
    }

    &:first-child,
    &:last-child {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    &:hover {
      box-shadow: none;
      color: #0B2033;
      
      body[data-theme='dark'] & {
        color: #FFFFFF;
      }
      font-weight: 400;
      background: transparent;
      opacity: 0.9;
    }

    & .MuiTab-wrapper,
    & .wrapper {
      padding: ${rem('10px')} ${rem('10px')} ${rem('12px')};
      line-height: 1.3;
      border-bottom: 2px solid transparent;
      max-width: none !important;
      overflow: visible;
      text-overflow: clip;
      white-space: nowrap;
    }

    &.Mui-selected,
    &.selected {
      background: transparent !important;
      box-shadow: none !important;
      font-weight: 600;
      color: #FF6300;

      &::before {
        display: none !important;
      }

      .MuiTab-wrapper,
      .wrapper {
        border-bottom-color: #ff6300;
      }

      &:hover {
        color: #FF6300;
        background: transparent;
        font-weight: 600;
      }
    }
  }
`;

/** Pipe-separated tabs subheader (<1024px). */
export const SummaryMobileTabsBar = styled.div`
  display: none;

  @media (max-width: 1023px) {
    display: flex;
    align-items: stretch;
    width: 100%;
    height: ${rem('48px')};
    box-sizing: border-box;
    background: #ffffff;
    
    body[data-theme='dark'] & {
      background: #0B2033;
    }
    box-shadow: 1px 2px 6px rgba(0, 0, 0, 0.2);
    flex-shrink: 0;
  }
`;

export const SummaryMobileTab = styled.button`
  flex: 1 1 0;
  min-width: 0;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0 ${rem('6px')};
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: ${rem('12px')};
  font-weight: ${props => (props.$active ? 600 : 400)};
  line-height: 1.25;
  text-align: center;
  color: ${props => (props.$active ? '#5B9BD5' : '#7A8494')};

  body[data-theme='dark'] & {
    color: ${props => (props.$active ? '#5FA8FF' : 'rgba(255, 255, 255, 0.55)')};
  }

  span {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 18%;
    bottom: 18%;
    right: 0;
    width: 1px;
    background: rgba(0, 0, 0, 0.12);
    pointer-events: none;
  }

  body[data-theme='dark'] &:not(:last-child)::after {
    background: rgba(255, 255, 255, 0.18);
  }

  &:hover {
    color: ${props => (props.$active ? '#5B9BD5' : '#4B5563')};
  }

  body[data-theme='dark'] &:hover {
    color: ${props => (props.$active ? '#5FA8FF' : 'rgba(255, 255, 255, 0.85)')};
  }

  &:focus {
    outline: none;
  }
`;

export const SummarySearchWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem('12px')};
  flex: 0 1 auto;
  min-width: 0;
  align-self: center;
  margin-top: -15px;

  @media (max-width: 1199px) {
    max-width: 55%;
  }

  @media (max-width: 1023px) {
    max-width: none;
    flex: 1 1 auto;
    width: 100%;
    gap: ${rem('8px')};
    margin-top: 0;
  }
`;

export const SummarySearchField = styled.div`
  flex: 0 0 ${rem('400px')};
  width: ${rem('400px')};
  max-width: 100%;
  min-width: ${rem('200px')};

  @media (max-width: 1199px) {
    flex: 0 0 ${rem('280px')};
    width: ${rem('280px')};
  }

  @media (max-width: 1023px) {
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
    max-width: none;
  }
`;

export const SummaryFiltersBar = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${rem('16px')};
  margin-top: ${rem('15px')};
  padding-bottom: ${rem('16px')};
  flex-shrink: 0;
`;

export const SummaryFiltersGroup = styled.div`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${rem('8px')};
  min-width: 0;
`;

export const SummaryFilterField = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem('6px')};
`;

export const SummaryFilterLabel = styled.span`
  font-size: ${rem('10px')};
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  line-height: 1.2;
  color: #475569;
  
  body[data-theme='dark'] & {
    color: #6DAFD9;
  }
  padding-left: ${rem('8px')};
`;

export const SummaryDownloadSlot = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
`;

export const EmptyListContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: ${rem('12px')};
  width: 100%;
  height: 100%;
  min-height: ${rem('280px')};
  padding: ${rem('32px')} ${rem('24px')};
  box-sizing: border-box;
`;

export const EmptyVisual = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem('96px')};
  height: ${rem('96px')};
  margin-bottom: ${rem('8px')};
  border-radius: ${rem('24px')};
  --empty-icon-surface: #ffffff;
  background: rgba(255, 99, 0, 0.08);
  border: 1px solid rgba(255, 99, 0, 0.18);

  &[data-variant='dismissed'] {
    background: rgba(255, 48, 48, 0.08);
    border-color: rgba(255, 48, 48, 0.2);
  }

  &[data-variant='search'] {
    background: rgba(109, 175, 217, 0.12);
    border-color: rgba(109, 175, 217, 0.2);
  }

  &[data-variant='history'] {
    background: rgba(95, 168, 255, 0.1);
    border-color: rgba(95, 168, 255, 0.2);
  }

  body[data-theme='dark'] & {
    --empty-icon-surface: #122c48;

    &[data-variant='search'] {
      background: rgba(109, 175, 217, 0.08);
    }

    &[data-variant='history'] {
      background: rgba(95, 168, 255, 0.08);
    }
  }

  svg {
    width: ${rem('56px')};
    height: ${rem('56px')};
    color: #6DAFD9;
    
    body[data-theme='dark'] & {
      color: rgba(109, 175, 217, 0.75);
    }
  }
`;

export const EmptyListTitle = styled(Typography)`
  color: ${props => props.theme.colors.S[800]};
  
  body[data-theme='dark'] & {
    color: #ffffff;
  }
  margin: 0;
  font-size: ${rem('20px')};
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
`;

export const EmptyListSubtitle = styled(Typography)`
  color: ${props => props.theme.colors.S[600]};
  
  body[data-theme='dark'] & {
    color: rgba(255, 255, 255, 0.55);
  }
  margin: 0;
  max-width: ${rem('360px')};
  font-size: ${rem('14px')};
  font-weight: 400;
  line-height: 1.5;
  text-align: center;
`;

/** Misma línea visual que la tabla de Productos: cabecera gris #eee, celdas con borde inferior. */
const TABLE_ROW_BORDER = '#e8eaed';
const TABLE_HEAD_HEIGHT = '25px';
const CHECKBOX_COL_PAD_LEFT = '13px';
const TABLE_CELL_PAD_X = rem('16px');
const TABLE_CELL_PAD_X_NARROW = rem('2px');

export const SummaryTablePanel = styled.div`
  flex: 1 1 0%;
  min-width: 0;
  min-height: 0;
  height: 100%;
  width: 100%;
  margin: ${rem('10px')} 0 0 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  
  body[data-theme='dark'] & {
    background: #072449;
  }
  border-radius: ${rem('4px')};
  overflow: hidden;
  -webkit-box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.2);
  box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.2);

  /* Misma distribución de columnas en todas las pestañas (con / sin columna checkbox según rol). */
  ${props =>
    props.$hasCheckbox &&
    css`
      .MuiTableHead-root .MuiTableCell-root:nth-child(1),
      .MuiTableBody-root .MuiTableCell-root:nth-child(1) {
        width: 40px;
        max-width: 44px;
        min-width: 36px;
        padding-left: ${CHECKBOX_COL_PAD_LEFT} !important;
        padding-right: ${TABLE_CELL_PAD_X_NARROW} !important;
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(2),
      .MuiTableBody-root .MuiTableCell-root:nth-child(2) {
        width: 11%;
        min-width: ${rem('118px')};
        padding-left: ${rem('4px')} !important;
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(3),
      .MuiTableBody-root .MuiTableCell-root:nth-child(3) {
        width: 14%;
        min-width: ${rem('140px')};
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(4),
      .MuiTableBody-root .MuiTableCell-root:nth-child(4) {
        width: 11%;
        min-width: ${rem('96px')};
        max-width: 11%;
        overflow: hidden;
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(4) .text,
      .MuiTableBody-root .MuiTableCell-root:nth-child(4) .text {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(5),
      .MuiTableBody-root .MuiTableCell-root:nth-child(5) {
        width: 7%;
        max-width: ${rem('100px')};
        min-width: ${rem('56px')};
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(6),
      .MuiTableBody-root .MuiTableCell-root:nth-child(6),
      .MuiTableHead-root .MuiTableCell-root:nth-child(7),
      .MuiTableBody-root .MuiTableCell-root:nth-child(7) {
        width: 10%;
        min-width: ${rem('112px')};
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(8),
      .MuiTableBody-root .MuiTableCell-root:nth-child(8) {
        width: 12%;
        min-width: ${rem('168px')};
        overflow: visible;
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(9),
      .MuiTableBody-root .MuiTableCell-root:nth-child(9) {
        width: 12%;
        min-width: ${rem('124px')};
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(10),
      .MuiTableBody-root .MuiTableCell-root:nth-child(10) {
        width: 13%;
        min-width: ${rem('148px')};
      }
    `}

  ${props =>
    !props.$hasCheckbox &&
    css`
      .MuiTableHead-root .MuiTableCell-root:nth-child(1),
      .MuiTableBody-root .MuiTableCell-root:nth-child(1) {
        width: 11%;
        min-width: ${rem('118px')};
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(2),
      .MuiTableBody-root .MuiTableCell-root:nth-child(2) {
        width: 15%;
        min-width: ${rem('140px')};
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(3),
      .MuiTableBody-root .MuiTableCell-root:nth-child(3) {
        width: 12%;
        min-width: ${rem('96px')};
        max-width: 12%;
        overflow: hidden;
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(3) .text,
      .MuiTableBody-root .MuiTableCell-root:nth-child(3) .text {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(4),
      .MuiTableBody-root .MuiTableCell-root:nth-child(4) {
        width: 8%;
        max-width: ${rem('100px')};
        min-width: ${rem('56px')};
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(5),
      .MuiTableBody-root .MuiTableCell-root:nth-child(5),
      .MuiTableHead-root .MuiTableCell-root:nth-child(6),
      .MuiTableBody-root .MuiTableCell-root:nth-child(6) {
        width: 10%;
        min-width: ${rem('112px')};
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(7),
      .MuiTableBody-root .MuiTableCell-root:nth-child(7) {
        width: 12%;
        min-width: ${rem('168px')};
        overflow: visible;
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(8),
      .MuiTableBody-root .MuiTableCell-root:nth-child(8) {
        width: 12%;
        min-width: ${rem('124px')};
      }
      .MuiTableHead-root .MuiTableCell-root:nth-child(9),
      .MuiTableBody-root .MuiTableCell-root:nth-child(9) {
        width: 13%;
        min-width: ${rem('148px')};
      }
    `}

  .MuiTable-root {
    background: #fff;
    
    body[data-theme='dark'] & {
      background: #072449;
    }
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
    width: 100%;
    max-width: 100%;
  }

  .MuiTableHead-root .MuiTableRow-root {
    height: ${TABLE_HEAD_HEIGHT} !important;
    max-height: ${TABLE_HEAD_HEIGHT} !important;
    min-height: ${TABLE_HEAD_HEIGHT} !important;
    box-sizing: border-box;
  }

  .MuiTableHead-root .MuiTableCell-root {
    background-color: #eeeeee !important;
    
    body[data-theme='dark'] & {
      background-color: #263E59 !important;
    }
    border-bottom: 1px solid ${TABLE_ROW_BORDER};

  body[data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
    font-weight: 700;
    height: ${TABLE_HEAD_HEIGHT} !important;
    max-height: ${TABLE_HEAD_HEIGHT} !important;
    min-height: ${TABLE_HEAD_HEIGHT} !important;
    box-sizing: border-box !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    padding-left: ${TABLE_CELL_PAD_X} !important;
    padding-right: ${TABLE_CELL_PAD_X} !important;
    vertical-align: middle;
    line-height: ${TABLE_HEAD_HEIGHT};
    overflow: hidden;
    color: #1a1f2e !important;
    
    body[data-theme='dark'] & {
      color: #6DAFD9 !important;
    }
    text-transform: uppercase;
    font-size: ${rem('11px')} !important;
  }

  .MuiTableHead-root .MuiTableCell-root:first-child,
  .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root:first-child {
    padding-left: ${CHECKBOX_COL_PAD_LEFT} !important;
    padding-right: ${TABLE_CELL_PAD_X_NARROW} !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    vertical-align: middle;
    text-align: left;
    cursor: pointer;
  }

  .MuiTableHead-root .MuiTableCell-root:first-child .MuiCheckbox-root,
  .MuiTableBody-root .MuiTableCell-root:first-child .MuiCheckbox-root {
    padding: 0 !important;
    margin: 0;
    vertical-align: middle;
  }

  .MuiTableHead-root .MuiTableCell-root:first-child .MuiIconButton-label,
  .MuiTableBody-root .MuiTableCell-root:first-child .MuiIconButton-label {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .MuiTableHead-root .MuiTableCell-root:first-child .MuiIconButton-label > span,
  .MuiTableBody-root .MuiTableCell-root:first-child .MuiIconButton-label > span {
    width: ${rem('14px')} !important;
    height: ${rem('14px')} !important;
  }

  .MuiTableHead-root .MuiTableCell-root:first-child .MuiIconButton-label > span svg,
  .MuiTableBody-root .MuiTableCell-root:first-child .MuiIconButton-label > span svg {
    width: ${rem('14px')};
    height: ${rem('14px')};
  }

  .MuiTableHead-root .MuiTableCell-root:first-child .MuiIconButton-label > span::after,
  .MuiTableBody-root .MuiTableCell-root:first-child .MuiIconButton-label > span::after {
    width: 6px;
    height: 1.5px;
  }

  .MuiTableHead-root .MuiTableCell-root.MuiTableCell-alignCenter {
    text-align: center;
  }

  .MuiTableHead-root .MuiTableCell-root.MuiTableCell-alignRight {
    text-align: right;
    padding-right: ${rem('16px')} !important;
  }

  .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root.MuiTableCell-alignCenter {
    text-align: center !important;
  }

  .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root.MuiTableCell-alignCenter .text {
    display: inline-block;
    width: auto;
    max-width: 100%;
  }

  .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root.MuiTableCell-alignRight,
  .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root[data-content-type='genericTotal'] {
    text-align: right !important;
  }

  .MuiTableHead-root .MuiTableCell-root.MuiTableCell-stickyHeader {
    background-color: #eeeeee !important;
    
    body[data-theme='dark'] & {
      background-color: #263E59 !important;
    }
  }

  .MuiTableHead-root .MuiTableCell-root.MuiTableCell-alignRight .MuiTableSortLabel-root {
    flex-direction: row !important;
  }

  .MuiTableHead-root .MuiTableCell-root[data-content-type='actions'],
  .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root[data-content-type='actions'] {
    overflow: visible !important;
    white-space: nowrap;
  }

  .MuiTableHead-root .MuiTableSortLabel-root {
    font-weight: 700;
    font-size: inherit;
    color: #1a1f2e !important;
    
    body[data-theme='dark'] & {
      color: #6DAFD9 !important;
    }
  }

  .MuiTableHead-root .MuiTableSortLabel-icon {
    color: #5f6368 !important;
    
    body[data-theme='dark'] & {
      color: rgba(109, 175, 217, 0.75) !important;
    }
    font-size: ${rem('16px')} !important;
    margin-left: ${rem('2px')};
  }

  .MuiTableBody-root .MuiTableRow-root {
    min-height: ${rem('56px')} !important;
    height: auto !important;
    max-height: none !important;
    overflow: hidden;
  }

  .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root {
    background: #fff;
    
    body[data-theme='dark'] & {
      background: #072449;
    }
    border-bottom: 1px solid ${TABLE_ROW_BORDER};

  body[data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
    min-height: ${rem('56px')} !important;
    box-sizing: border-box !important;
    padding-top: ${rem('6px')} !important;
    padding-bottom: ${rem('6px')} !important;
    padding-left: ${TABLE_CELL_PAD_X} !important;
    padding-right: ${TABLE_CELL_PAD_X} !important;
    overflow: hidden;
    vertical-align: middle;
    line-height: ${rem('18px')};
    color: inherit;
    
    body[data-theme='dark'] & {
      color: #ffffff;
    }
  }

  .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root .text {
    color: inherit;
  }

  /* Desktop / mobile row layouts */
  .summary-row-mobile {
    display: none !important;
  }

  @media (max-width: 1023px) {
    .MuiTableHead-root {
      display: none;
    }

    .MuiTable-root {
      table-layout: auto;
    }

    .MuiTableBody-root .MuiTableRow-root {
      display: table-row;
    }

    .summary-row-desktop {
      display: none !important;
    }

    .summary-row-mobile {
      display: table-cell !important;
      width: 100% !important;
      max-width: none !important;
      min-width: 0 !important;
      padding: ${rem('8px')} ${rem('12px')} !important;
      border-bottom: 1px solid ${TABLE_ROW_BORDER} !important;
      
      body[data-theme='dark'] & {
        border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
      }
      vertical-align: top !important;
      overflow: visible !important;
    }

    .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root.summary-row-mobile:before {
      display: none;
    }
  }

  /* Total: gold en dark; slate en light */
  .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root[data-content-type='genericTotal'] .text {
    display: inline-block;
    width: auto;
    max-width: 100%;
    color: #1E293B !important;
    
    body[data-theme='dark'] & {
      color: #e9cb7a !important;
    }
    font-weight: 600 !important;
    
    body[data-theme='dark'] & {
      font-weight: 700 !important;
    }
    white-space: nowrap;
  }

  .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root[data-content-type='genericTotal'] .plus-tax {
    color: #64748B !important;
    
    body[data-theme='dark'] & {
      color: #ffffff !important;
    }
    font-size: ${rem('10px')};
    font-weight: 600 !important;
    
    body[data-theme='dark'] & {
      font-weight: 700 !important;
    }
  }

  body[data-theme='dark'] & {
      .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root[data-content-type='number'] .text {
        color: #e9cb7a !important;
        font-weight: 600;
      }
      }

  .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root:first-child {
    padding-left: ${CHECKBOX_COL_PAD_LEFT} !important;
    padding-right: ${TABLE_CELL_PAD_X_NARROW} !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    vertical-align: middle;
  }

  ${props =>
    props.$hasCheckbox &&
    css`
      .MuiTableHead-root .MuiTableCell-root:nth-child(2),
      .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root:nth-child(2) {
        padding-left: ${rem('4px')} !important;
      }
    `}

  .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root[data-content-type='status'] {
    overflow: visible !important;
  }

  .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root {
    background-color: #f8f9fa !important;
    
    body[data-theme='dark'] & {
      background-color: #0a2f5c !important;
    }
  }

  .MuiTableBody-root .MuiTableRow-root.Mui-selected .MuiTableCell-root {
    background-color: #e8f0fe !important;
    
    body[data-theme='dark'] & {
      background-color: #0d3568 !important;
    }
  }

  .MuiTableBody-root .MuiTableRow-root.Mui-selected:hover .MuiTableCell-root {
    background-color: #e8f0fe !important;
    
    body[data-theme='dark'] & {
      background-color: #0d3568 !important;
    }
  }

  .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root {
    border-bottom: none;
  }
`;

export function ExportDownloadIcon(props) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M12 4v10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 10l4 4 4-4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 20h14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const SummaryExportXlsButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${rem('8px')};
  height: ${rem('36px')};
  padding: 0 ${rem('14px')};
  border-radius: ${rem('6px')};
  border: 1px solid #E2E8F0;
  
  body[data-theme='dark'] & {
    border: 1px solid rgba(255, 255, 255, 0.28);
  }
  background: #FFFFFF;
  
  body[data-theme='dark'] & {
    background: transparent;
  }
  color: #1E293B;
  
  body[data-theme='dark'] & {
    color: #ffffff;
  }
  font-size: ${rem('14px')};
  font-weight: 400;
  font-family: inherit;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: none;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: #F8FAFC;

  body[data-theme='dark'] & {
    background: rgba(255, 255, 255, 0.06);
  }
    border-color: #E2E8F0;
  }

  body[data-theme='dark'] &:hover {
    border-color: rgba(255, 255, 255, 0.45);
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.G[900]};
    outline-offset: 2px;
  }

  body[data-theme='dark'] &:focus-visible {
    outline-color: rgba(255, 255, 255, 0.35);
  }

  svg {
    flex-shrink: 0;
    display: block;
  }
`;

/** Área scroll de la tabla + overlay de carga (mismo patrón que Productos). */
export const SummaryTableScrollArea = styled.div`
  position: relative;
  flex: 1 1 0%;
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const SummaryTableLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.55);
  
  body[data-theme='dark'] & {
    background: rgba(7, 36, 73, 0.65);
  }
  pointer-events: none;
  z-index: 2;
`;

export const SummaryBottomBar = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 1fr) auto minmax(160px, 1fr);
  align-items: center;
  gap: ${rem('16px')};
  width: 100%;
  min-height: ${rem('52px')};
  margin-top: ${rem('8px')};
  padding: ${rem('8px')} 0 ${rem('4px')};
  flex-shrink: 0;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: ${rem('8px')};
    min-height: ${rem('44px')};
    padding: ${rem('6px')} 0;
  }
`;

export const SummaryPageSize = styled.label`
  display: flex;
  align-items: center;
  gap: ${rem('8px')};
  margin: 0;
  justify-self: start;
  font-size: ${rem('12px')};
  color: ${props => props.theme.colors.S[700]};
  
  body[data-theme='dark'] & {
    color: rgba(255, 255, 255, 0.55);
  }
  white-space: nowrap;

  @media (max-width: 1023px) {
    display: none;
  }

  span {
    line-height: 1;
  }

  select {
    height: ${rem('36px')};
    width: ${rem('72px')};
    border-radius: ${rem('6px')};
    border: 1px solid #e0e0e0;
    background-color: #fff;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23666666' fill-opacity='0.7' d='M1 1l4 4 4-4'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    color: ${props => props.theme.colors.S[800]};
    padding: 8px 28px 8px 10px;
    appearance: none;
    outline: none;
    font-size: ${rem('13px')};
    cursor: pointer;
  }

  body[data-theme='dark'] & select {
    border: 1px solid rgba(30, 80, 255, 0.14);
    background-color: #082952;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23ffffff' fill-opacity='0.7' d='M1 1l4 4 4-4'/%3E%3C/svg%3E");
    color: #fff;
  }
`;

export const SummaryBottomBarPagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: center;

  .rc-pagination {
    display: flex;
    align-items: center;
    margin: 0;
    padding: 0;
  }

  .rc-pagination-item,
  .rc-pagination-prev,
  .rc-pagination-next,
  .rc-pagination-jump-prev,
  .rc-pagination-jump-next {
    min-width: ${rem('28px')};
    height: ${rem('28px')};
    line-height: ${rem('28px')};
    margin: 0 ${rem('2px')};
    border: none;
    border-radius: ${rem('6px')};
    background: transparent;
    font-size: ${rem('14px')};
    color: #7d7d7d;
    
    body[data-theme='dark'] & {
      color: rgba(255, 255, 255, 0.65);
    }
  }

  .rc-pagination-item a {
    color: inherit;
  }

  .rc-pagination-item:hover,
  .rc-pagination-prev:hover,
  .rc-pagination-next:hover {
    background: #eeeeee;

  body[data-theme='dark'] & {
    background: rgba(255, 255, 255, 0.08);
  }
    color: #646464;
    
    body[data-theme='dark'] & {
      color: #fff;
    }
  }

  .rc-pagination-item-active,
  .rc-pagination-item-active:hover {
    background: #dcedff;

  body[data-theme='dark'] & {
    background: rgba(95, 168, 255, 0.25);
  }
    color: #0171e6;
    
    body[data-theme='dark'] & {
      color: #5fa8ff;
    }
  }

  .rc-pagination-item-active a {
    color: inherit;
  }

  .rc-pagination-disabled,
  .rc-pagination-disabled:hover {
    background: transparent;
    opacity: 0.35;
    cursor: default;
  }

  /* Compact / simple pager */
  .rc-pagination-simple {
    display: inline-flex;
    align-items: center;
    gap: ${rem('4px')};
  }

  .rc-pagination-simple .rc-pagination-simple-pager {
    display: inline-flex;
    align-items: center;
    gap: ${rem('6px')};
    margin: 0 ${rem('4px')};
    font-size: ${rem('13px')};
    color: #5f6368;
    
    body[data-theme='dark'] & {
      color: rgba(255, 255, 255, 0.75);
    }
  }

  .rc-pagination-simple .rc-pagination-simple-pager input {
    width: ${rem('36px')};
    height: ${rem('28px')};
    margin: 0;
    padding: 0 ${rem('4px')};
    border: 1px solid #e0e0e0;
    
    body[data-theme='dark'] & {
      border: 1px solid rgba(255, 255, 255, 0.16);
    }
    border-radius: ${rem('6px')};
    background: #fff;
    
    body[data-theme='dark'] & {
      background: #082952;
    }
    color: #1a1f2e;
    
    body[data-theme='dark'] & {
      color: #fff;
    }
    text-align: center;
    font-size: ${rem('13px')};
  }
`;

export const SummaryResultsCount = styled.div`
  justify-self: end;
  text-align: right;
  min-width: ${rem('180px')};
  font-size: ${rem('12px')};
  color: ${props => props.theme.colors.S[700]};
  
  body[data-theme='dark'] & {
    color: rgba(255, 255, 255, 0.55);
  }
  white-space: nowrap;

  @media (max-width: 1023px) {
    display: none;
  }
`;

const skeletonShimmer = css`
  @keyframes summarySkelShimmer {
    0% {
      background-position: 300% 0;
    }
    100% {
      background-position: -300% 0;
    }
  }
`;

export const SummarySkeletonRoot = styled.div`
  ${skeletonShimmer}
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  
  body[data-theme='dark'] & {
    background: #072449;
  }
`;

export const SummarySkeletonHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem('12px')};
  height: ${TABLE_HEAD_HEIGHT};
  padding: 0 ${rem('16px')} 0 ${CHECKBOX_COL_PAD_LEFT};
  background: #eeeeee;
  
  body[data-theme='dark'] & {
    background: #263E59;
  }
  border-bottom: 1px solid #e8eaed;
  
  body[data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
  flex-shrink: 0;
`;

export const SummarySkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem('12px')};
  min-height: ${rem('56px')};
  padding: ${rem('8px')} ${rem('16px')} ${rem('8px')} ${CHECKBOX_COL_PAD_LEFT};
  border-bottom: 1px solid #e8eaed;
  
  body[data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
  box-sizing: border-box;

  & > * {
    animation-delay: ${props => props.$delay || '0s'};
  }
`;

export const SummarySkeletonBar = styled.div`
  height: ${props => props.$height || rem('12px')};
  width: ${props => props.$width || '100%'};
  max-width: ${props => props.$maxWidth || '100%'};
  border-radius: ${props => props.$radius || rem('4px')};
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.1) 40%, rgba(0, 0, 0, 0.04) 80%);

  body[data-theme='dark'] & {
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.14) 40%, rgba(255, 255, 255, 0.04) 80%);
  }
  background-size: 300% 100%;
  animation: summarySkelShimmer 1.8s ease-in-out infinite;
  flex-shrink: 0;
`;

export const SummarySkeletonCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem('6px')};
  min-width: 0;
  flex: ${props => props.$flex || '1 1 0%'};
  width: ${props => props.$width || 'auto'};
  max-width: ${props => props.$maxWidth || 'none'};
`;

export const SummarySkeletonActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem('6px')};
  flex: 0 0 13%;
  width: 13%;
  min-width: ${rem('148px')};
`;

export const SummarySkeletonCircle = styled.div`
  width: ${rem('28px')};
  height: ${rem('28px')};
  border-radius: 50%;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.1) 40%, rgba(0, 0, 0, 0.04) 80%);

  body[data-theme='dark'] & {
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.14) 40%, rgba(255, 255, 255, 0.04) 80%);
  }
  background-size: 300% 100%;
  animation: summarySkelShimmer 1.8s ease-in-out infinite;
  flex-shrink: 0;
`;

const SKEL_ROWS = 6;

function EmptyStateIcon({ name, surface }) {
  if (name === 'pending') {
    return (
      <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
        <rect x="22" y="12" width="36" height="56" rx="6" stroke="currentColor" strokeWidth="2.5" />
        <path d="M30 12h20v8H30z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
        <path d="M32 36h16M32 46h16M32 56h10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="56" cy="56" r="12" fill={surface} stroke="#FF6300" strokeWidth="2.5" />
        <path d="M50.5 56.5l3.5 3.5 7-8" stroke="#FF6300" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === 'next') {
    return (
      <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
        <rect x="16" y="20" width="48" height="44" rx="8" stroke="currentColor" strokeWidth="2.5" />
        <path d="M16 34h48" stroke="currentColor" strokeWidth="2.5" />
        <path d="M28 14v12M52 14v12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="32" cy="48" r="3" fill="#FF6300" />
        <circle cx="40" cy="48" r="3" fill="currentColor" opacity="0.45" />
        <circle cx="48" cy="48" r="3" fill="currentColor" opacity="0.45" />
        <circle cx="32" cy="58" r="3" fill="currentColor" opacity="0.45" />
        <circle cx="40" cy="58" r="3" fill="#FF6300" />
      </svg>
    );
  }
  if (name === 'history') {
    return (
      <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
        <circle cx="40" cy="42" r="22" stroke="currentColor" strokeWidth="2.5" />
        <path d="M40 28v14l10 6" stroke="#FF6300" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M28 16h24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <path d="M40 12v6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === 'dismissed') {
    return (
      <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
        <rect x="18" y="14" width="44" height="52" rx="8" stroke="currentColor" strokeWidth="2.5" />
        <path d="M28 30h24M28 40h24M28 50h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="56" cy="56" r="14" fill={surface} stroke="#FF3030" strokeWidth="2.5" />
        <path d="M50 50l12 12M62 50L50 62" stroke="#FF3030" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === 'search') {
    return (
      <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
        <circle cx="36" cy="36" r="16" stroke="currentColor" strokeWidth="2.5" />
        <path d="M48 48l14 14" stroke="#FF6300" strokeWidth="3" strokeLinecap="round" />
        <path d="M30 36h12M36 30v12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" opacity="0.5" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
      <rect x="24" y="22" width="38" height="46" rx="6" stroke="currentColor" strokeWidth="2.5" />
      <rect x="18" y="16" width="38" height="46" rx="6" fill={surface} stroke="currentColor" strokeWidth="2.5" />
      <path d="M28 30h18M28 40h18M28 50h12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M48 58h10" stroke="#FF6300" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

export function SummaryEmptyPlaceholder({ icon = 'all', title, subtitle }) {
  return (
    <EmptyListContainer>
      <EmptyVisual data-variant={icon}>
        <EmptyStateIcon name={icon} surface="var(--empty-icon-surface)" />
      </EmptyVisual>
      {title ? <EmptyListTitle>{title}</EmptyListTitle> : null}
      {subtitle ? <EmptyListSubtitle>{subtitle}</EmptyListSubtitle> : null}
    </EmptyListContainer>
  );
}

export function SummaryTableSkeleton({ hasCheckbox = true }) {
  return (
    <SummarySkeletonRoot role="status" aria-busy="true" aria-label="Loading">
      <SummarySkeletonHead>
        {hasCheckbox && <SummarySkeletonBar $height={rem('14px')} $width={rem('14px')} />}
        <SummarySkeletonBar $height={rem('10px')} $width="11%" />
        <SummarySkeletonBar $height={rem('10px')} $width="14%" />
        <SummarySkeletonBar $height={rem('10px')} $width="11%" />
        <SummarySkeletonBar $height={rem('10px')} $width="7%" />
        <SummarySkeletonBar $height={rem('10px')} $width="10%" />
        <SummarySkeletonBar $height={rem('10px')} $width="10%" />
        <SummarySkeletonBar $height={rem('10px')} $width="12%" />
        <SummarySkeletonBar $height={rem('10px')} $width="12%" />
        <SummarySkeletonBar $height={rem('10px')} $width="13%" />
      </SummarySkeletonHead>
      {Array.from({ length: SKEL_ROWS }).map((_, i) => (
        <SummarySkeletonRow key={i} $delay={`${i * 0.08}s`}>
          {hasCheckbox && (
            <SummarySkeletonBar $height={rem('14px')} $width={rem('14px')} />
          )}
          <SummarySkeletonCol $width="11%" $maxWidth="11%" $flex="0 0 11%">
            <SummarySkeletonBar $height={rem('14px')} $width="72%" />
          </SummarySkeletonCol>
          <SummarySkeletonCol $flex="1 1 14%">
            <SummarySkeletonBar $height={rem('12px')} $width="88%" />
          </SummarySkeletonCol>
          <SummarySkeletonCol $flex="1 1 11%">
            <SummarySkeletonBar $height={rem('12px')} $width="78%" />
          </SummarySkeletonCol>
          <SummarySkeletonCol $width="7%" $flex="0 0 7%">
            <SummarySkeletonBar $height={rem('12px')} $width="70%" />
          </SummarySkeletonCol>
          <SummarySkeletonCol $width="10%" $flex="0 0 10%">
            <SummarySkeletonBar $height={rem('12px')} $width="82%" />
          </SummarySkeletonCol>
          <SummarySkeletonCol $width="10%" $flex="0 0 10%">
            <SummarySkeletonBar $height={rem('12px')} $width="82%" />
          </SummarySkeletonCol>
          <SummarySkeletonCol $width="12%" $flex="0 0 12%">
            <SummarySkeletonBar $height={rem('22px')} $width={rem('88px')} $radius={rem('11px')} />
          </SummarySkeletonCol>
          <SummarySkeletonCol $width="12%" $flex="0 0 12%">
            <SummarySkeletonBar $height={rem('14px')} $width="78%" />
            <SummarySkeletonBar $height={rem('8px')} $width="42%" />
          </SummarySkeletonCol>
          <SummarySkeletonActions>
            <SummarySkeletonCircle />
            <SummarySkeletonCircle />
            <SummarySkeletonCircle />
            <SummarySkeletonCircle />
          </SummarySkeletonActions>
        </SummarySkeletonRow>
      ))}
    </SummarySkeletonRoot>
  );
}

const SummaryPageSkeletonShell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: #f2f3f5;
  
  body[data-theme='dark'] & {
    background: transparent;
  }
`;

const SummaryCrumbSkeletonStrip = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem('10px')};
  height: ${rem('55px')};
  min-height: ${rem('55px')};
  padding: 0 ${rem('16px')};
  box-sizing: border-box;
  background: #ffffff;
  
  body[data-theme='dark'] & {
    background: #0B2033;
  }
  box-shadow: 1px 2px 6px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
`;

const SummaryPageSkeletonBody = styled.div`
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: ${rem('16px')};
  box-sizing: border-box;
  overflow: hidden;
`;

const SummaryTabsSkeletonRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${rem('16px')};
  margin: 0 ${rem('-16px')} ${rem('12px')};
  padding: 0 ${rem('16px')} ${rem('10px')};
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  
  body[data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
  flex-shrink: 0;
`;

const SummaryTabsSkeletonLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem('16px')};
  flex: 1 1 auto;
  min-width: 0;
`;

const SummaryTableSkeletonPanel = styled.div`
  flex: 1 1 0%;
  min-height: 0;
  margin-top: ${rem('10px')};
  border-radius: ${rem('4px')};
  overflow: hidden;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.2);
  background: #fff;
  
  body[data-theme='dark'] & {
    background: #072449;
  }
`;

/** Full-page skeleton for Suspense / initial /summary load (dark + light). */
export function SummaryPageSkeleton() {
  return (
    <SummaryPageSkeletonShell role="status" aria-busy="true" aria-label="Loading">
      <SummaryCrumbSkeletonStrip>
        <SummarySkeletonBar $height="12px" $width="90px" />
        <SummarySkeletonBar $height="12px" $width="140px" />
      </SummaryCrumbSkeletonStrip>
      <SummaryPageSkeletonBody>
        <SummaryTabsSkeletonRow>
          <SummaryTabsSkeletonLeft>
            <SummarySkeletonBar $height="14px" $width="110px" />
            <SummarySkeletonBar $height="14px" $width="120px" />
            <SummarySkeletonBar $height="14px" $width="120px" />
            <SummarySkeletonBar $height="14px" $width="90px" />
            <SummarySkeletonBar $height="14px" $width="100px" />
          </SummaryTabsSkeletonLeft>
          <SummarySkeletonBar $height="40px" $width="280px" $radius="7px" />
        </SummaryTabsSkeletonRow>
        <SummaryTableSkeletonPanel>
          <SummaryTableSkeleton hasCheckbox />
        </SummaryTableSkeletonPanel>
      </SummaryPageSkeletonBody>
    </SummaryPageSkeletonShell>
  );
}
